import { getSupabaseBrowserClient } from "../integrations/supabase/client";

/**
 * Enrolling, replacing and removing the second factor.
 *
 * `app/auth/mfa.ts` decides whether a session still owes a factor and answers the challenge.
 * Enrollment was left out of it on purpose, with a note pointing here: build-plan.md keeps it in
 * Batch D with the rest of account settings. This is that half.
 *
 * Three things this file exists to get right, none of which the SDK does for you:
 *
 * **An abandoned enrollment leaves a factor behind.** `mfa.enroll` creates an unverified TOTP factor
 * the moment the QR code is drawn. Somebody who opens the panel, thinks better of it and navigates
 * away leaves that row on their account, and GoTrue caps the number of factors per user. Doing it
 * five times would eventually refuse the sixth attempt with an error about a limit nobody knew
 * existed. So `startEnrollment` clears unverified factors before it creates a new one, and the panel
 * clears them again when it closes.
 *
 * **Replacing a factor is not removing one and adding one.** A person who has lost their
 * authenticator cannot reach this screen at all, because an `aal1` session with a verified factor is
 * sent to `/verify` and stays there. What this file supports is the case that is actually
 * reachable: somebody who still has the current factor and wants a different one, on a new phone.
 * The new factor is enrolled and verified first, and the old one is removed only after the new one
 * has been proved. Removing first would leave a Petrovic admin one interrupted request away from an
 * account with no factor and a requirement for one.
 *
 * **Verification is a challenge and a verify, not a code comparison.** The same two-step exchange
 * `VerificationRoute` uses at sign-in, because it is the same GoTrue mechanism.
 */

export interface EnrollmentStart {
  factorId: string;
  /** A `data:` image of the provisioning URI, produced by GoTrue. */
  qrCode: string;
  /** The same value in text, for an authenticator app that cannot scan. */
  secret: string;
}

export type EnrollmentFailure =
  | "code-wrong"
  | "code-expired"
  | "factor-limit"
  | "unavailable";

export const ENROLLMENT_FAILURE_TEXT: Record<EnrollmentFailure, string> = {
  "code-wrong":
    "Koden stämmer inte. Kontrollera att appen visar koden för det här kontot, och att telefonens klocka går rätt.",
  "code-expired": "Koden hann gå ut. Ange koden som appen visar nu.",
  "factor-limit":
    "Kontot har redan så många faktorer som går att lägga till. Ta bort en av dem först.",
  unavailable: "Det gick inte just nu. Försök igen."
};

function classify(error: { code?: string; message?: string } | null): EnrollmentFailure {
  const code = error?.code ?? "";
  const message = (error?.message ?? "").toLowerCase();

  if (code === "mfa_challenge_expired" || message.includes("expired")) {
    return "code-expired";
  }
  if (code === "mfa_verification_failed" || message.includes("invalid")) {
    return "code-wrong";
  }
  if (code === "mfa_factor_name_conflict" || message.includes("maximum") || message.includes("limit")) {
    return "factor-limit";
  }
  return "unavailable";
}

/**
 * The cache key for the answer below.
 *
 * Exported rather than declared twice, because two panels on `/settings` read it: the factor panel
 * decides what to offer, and the management panel decides whether a step-up can be proved at all.
 * Enrolling a factor in the first has to reach the second without a reload.
 */
/** The issuer an authenticator app shows for the portal's factor (build round 32b, 2026-10-10). */
export const TOTP_ISSUER = "Petrovic Systems";

export const verifiedFactorKey = ["mfa", "factors"] as const;

/** The verified factor this account signs in with, or null when it has none. */
export async function verifiedFactorId(): Promise<string | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) {
    throw error;
  }
  // `listFactors` already filters `totp` down to the verified ones.
  return data.totp[0]?.id ?? null;
}

/**
 * Removes every TOTP factor that was started and never confirmed.
 *
 * Called before an enrollment and again when the panel closes, so an abandoned attempt cannot
 * accumulate. A failure here is swallowed: it is housekeeping, and reporting it would put an error
 * in front of somebody who has done nothing wrong.
 */
export async function clearUnverifiedFactors(): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) {
    return;
  }

  for (const factor of data.all) {
    if (factor.factor_type === "totp" && factor.status !== "verified") {
      await supabase.auth.mfa.unenroll({ factorId: factor.id }).catch(() => undefined);
    }
  }
}

export async function startEnrollment(): Promise<EnrollmentStart> {
  const supabase = getSupabaseBrowserClient();

  await clearUnverifiedFactors();

  // No `friendlyName`. GoTrue requires it to be unique per user, so a fixed one refuses the second
  // enrollment on an account that already has a factor, which is exactly the replacement case this
  // file is here to support.
  // `issuer` is the name an authenticator app shows beside the account. Without it GoTrue uses the
  // site URL's host. "Petrovic Systems" since 2026-10-10 (build round 32b); a factor enrolled earlier
  // keeps the label it was given.
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", issuer: TOTP_ISSUER });

  if (error || !data) {
    throw error ?? new Error("PORTAL_MFA_ENROLL_FAILED");
  }

  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/**
 * Confirms the new factor, then removes the one it replaces.
 *
 * `replaces` is the currently verified factor when this is a replacement and null on a first
 * enrollment. It is removed only after `verify` has succeeded, so an account that requires a factor
 * never spends a moment without one.
 */
export async function confirmEnrollment(
  factorId: string,
  code: string,
  replaces: string | null
): Promise<{ ok: true } | { ok: false; reason: EnrollmentFailure }> {
  const supabase = getSupabaseBrowserClient();

  const challenge = await supabase.auth.mfa.challenge({ factorId });
  if (challenge.error || !challenge.data) {
    return { ok: false, reason: classify(challenge.error) };
  }

  const verified = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.data.id,
    code: code.trim()
  });

  if (verified.error) {
    return { ok: false, reason: classify(verified.error) };
  }

  if (replaces && replaces !== factorId) {
    const removed = await supabase.auth.mfa.unenroll({ factorId: replaces });
    if (removed.error) {
      // The new factor is verified and usable, so this is not a failed enrollment. Leaving the old
      // one in place is safe: either code signs in. Saying "enrollment failed" here would be false
      // and would invite the person to do it again, which adds a third factor.
      return { ok: true };
    }
  }

  return { ok: true };
}

export async function removeFactor(factorId: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) {
    throw error;
  }
}
